import { useParams, Navigate } from "react-router-dom";
import MarketingPage from "@/components/public/MarketingPage";
import LifestyleVideo from "@/components/public/LifestyleVideo";
import { getMoment } from "@/data/lifestyle_moments";
import { FEATURE_PAGES, PRODUCT_PILLARS, TOGETHER_HERO, COUPLES_PAGE,
         FAMILIES_PAGE, GOALS_PAGE, PRIVACY_PAGE, CALENDAR_PAGE, INBOX_PAGE,
         HEALTH_PAGE, INTELLIGENCE_PAGE, ABOUT_PAGE, HERO_CTA,
         RESOURCES_CATEGORIES } from "@/data/marketing_content";
import StatusPill from "@/components/public/StatusPill";
import { Link } from "react-router-dom";
import { ARTICLES } from "@/data/resources_articles";

/* ─────────── /product ─────────── */
export function ProductPage() {
    return (
        <MarketingPage
            slug="product"
            title="Product"
            description="One VAULT. Your complete financial world — money, obligations, wealth, documents, household and insights."
            canonical="/product"
            hero="One VAULT. Your complete financial world."
            sub="From everyday payments and subscriptions to investments, assets, important documents and household responsibilities, VAULT gives you one structured place to organise what matters."
        >
            <section className="max-w-5xl mx-auto px-6 pb-16" data-testid="product-pillars">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PRODUCT_PILLARS.map(p => (
                        <div key={p.key} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`pillar-${p.key}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-800">{p.label}</div>
                                {p.gated && <StatusPill featureKey={p.gated} />}
                            </div>
                            <ul className="space-y-1.5">
                                {p.items.map(it => (
                                    <li key={it} className="text-[13px] text-stone-700 flex items-center gap-2">
                                        <span className="w-1 h-1 rounded-full bg-stone-400" />{it}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>
        </MarketingPage>
    );
}

/* ─────────── /features/:slug ─────────── */
export function FeaturePage() {
    const { slug } = useParams();
    const cfg = FEATURE_PAGES[slug];
    if (!cfg) return <Navigate to="/product" replace />;
    return (
        <MarketingPage
            slug={`feature-${slug}`}
            title={cfg.hero.replace(/[.·]/g, "").slice(0, 55)}
            description={cfg.sub}
            canonical={`/features/${slug}`}
            hero={cfg.hero}
            sub={cfg.sub}
            statusKey={cfg.statusKey}
            bullets={cfg.bullets}
            disclaimer={cfg.disclaimer}
            primaryCta={cfg.cta ? { label: cfg.cta, to: cfg.ctaTo || "/signup", testid: `feature-cta-${slug}` } : HERO_CTA.primary}
        />
    );
}

/* ─────────── /together ─────────── */
export function TogetherPage() {
    return (
        <MarketingPage
            slug="together"
            title="VAULT Together — Mine. Yours. Ours."
            description="Organise a shared financial life without giving up individual privacy. Shared where it matters, private where it counts."
            canonical="/together"
            hero="Mine. Yours. Ours."
            sub="Organise a shared financial life without giving up individual privacy."
            statusKey="vault_together"
        >
            <style>{TOGETHER_CSS}</style>

            {/* ── 0. HUMAN LIFESTYLE MOMENT ─────────────────────────── */}
            <LifestyleVideo moment={getMoment("together")} side="left" />

            {/* ── 1. THE FOUR LENSES ────────────────────────────────── */}
            <section className="tp-container tp-lenses" data-testid="together-lenses">
                <div className="tp-eyebrow">Four lenses, one workspace</div>
                <div className="tp-lenses__grid">
                    {[
                        { key: "mine",     tag: "MINE",     title: "Your private world",
                          body: "Your accounts, your subscriptions, your documents. Only you see this." },
                        { key: "partner",  tag: "PARTNER",  title: "Shared with you",
                          body: "Information your partner has chosen to share — never more, never less." },
                        { key: "joint",    tag: "JOINT",    title: "Owned together",
                          body: "Things you own, owe or manage as a household. Both of you have full visibility." },
                        { key: "together", tag: "TOGETHER", title: "Household view",
                          body: "A single shared view containing only what is authorised for sharing." },
                    ].map((l) => (
                        <div key={l.key} className={`tp-lens tp-lens--${l.key}`} data-testid={`lens-${l.key}`}>
                            <div className="tp-lens__tag">{l.tag}</div>
                            <h3 className="tp-lens__title">{l.title}</h3>
                            <p className="tp-lens__body">{l.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── 2. USE CASES ─────────────────────────────────────── */}
            <section className="tp-container tp-usecases" data-testid="together-usecases">
                <h2 className="tp-h2">One shared household, quietly organised.</h2>
                <p className="tp-h2-sub">
                    Common things families and couples already share — now in one place.
                </p>
                <div className="tp-usecases__grid">
                    {[
                        "Household subscriptions",
                        "Shared payments",
                        "Joint obligations",
                        "Family financial calendar",
                        "Important household documents",
                        "Shared assets",
                        "Shared liabilities",
                        "Upcoming renewals",
                    ].map((u) => (
                        <div key={u} className="tp-usecase" data-testid={`usecase-${u.toLowerCase().replace(/\s+/g,"-")}`}>
                            <span className="tp-usecase__dot" />{u}
                        </div>
                    ))}
                </div>
            </section>

            {/* ── 3. PRIVACY STORY ─────────────────────────────────── */}
            <section className="tp-privacy" data-testid="together-privacy">
                <div className="tp-container">
                    <div className="tp-eyebrow tp-eyebrow--gold">Privacy is the default</div>
                    <h2 className="tp-privacy__title">
                        Together when you want it.<br/>Private when you need it.
                    </h2>
                    <div className="tp-privacy__grid">
                        {[
                            { title: "Separate identities",     body: "Each person has their own account and login. Nothing is auto-merged." },
                            { title: "Private stays private",   body: "Personal accounts, subscriptions and documents never appear to your partner." },
                            { title: "Only what you share",     body: "Together only surfaces items you explicitly mark as shared, joint, or household." },
                            { title: "Reversible sharing",      body: "Sharing can be changed or revoked at any time — nothing is destroyed if you do." },
                        ].map((p) => (
                            <div key={p.title} className="tp-privacy__card" data-testid={`privacy-${p.title.toLowerCase().replace(/\s+/g,"-")}`}>
                                <div className="tp-privacy__card-title">{p.title}</div>
                                <p className="tp-privacy__card-body">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 4. CTA ───────────────────────────────────────────── */}
            <section className="tp-container tp-cta" data-testid="together-cta">
                <h2 className="tp-cta__title">Create your Family VAULT.</h2>
                <p className="tp-cta__sub">
                    Start organising the household&rsquo;s financial life in one place — with privacy exactly where it belongs.
                </p>
                <div className="tp-cta__row">
                    <Link to="/signup?plan=family" className="tp-btn tp-btn--gold" data-testid="together-cta-primary">
                        Create Your Family VAULT
                    </Link>
                    <Link to="/pricing" className="tp-btn tp-btn--ghost" data-testid="together-cta-secondary">
                        View Family plan
                    </Link>
                </div>
                <p className="tp-cta__fine">
                    No CVV. No PIN. No OTP. VAULT is an organisation tool, not a bank.
                </p>
            </section>
        </MarketingPage>
    );
}

const TOGETHER_CSS = `
.tp-container { max-width: 1160px; margin: 0 auto; padding: 0 28px; }
.tp-eyebrow { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #78716C; margin-bottom: 12px; }
.tp-eyebrow--gold { color: #C6A86B; }
.tp-h2 { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(30px, 4vw, 44px); font-weight: 500; letter-spacing: -0.015em; color: #0F1E3A; margin: 0 0 10px; line-height: 1.05; }
.tp-h2-sub { font-size: 15.5px; line-height: 1.55; color: #57534E; max-width: 560px; margin: 0 auto 36px; text-align: center; }
.tp-lenses { padding: 20px 28px 56px; }
.tp-lenses__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
@media (max-width: 900px) { .tp-lenses__grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .tp-lenses__grid { grid-template-columns: 1fr; } }
.tp-lens { position: relative; padding: 26px 22px; border-radius: 20px; border: 1px solid rgba(15,30,58,0.10); background: #FFFFFF; transition: transform .24s cubic-bezier(0.16,1,0.3,1), box-shadow .24s cubic-bezier(0.16,1,0.3,1); overflow: hidden; }
.tp-lens:hover { transform: translateY(-4px); box-shadow: 0 24px 48px -20px rgba(15,30,58,0.18); }
.tp-lens::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; }
.tp-lens--mine::before     { background: linear-gradient(90deg, #22D3EE, #14B8A6); }
.tp-lens--partner::before  { background: linear-gradient(90deg, #8B5CF6, #EC4899); }
.tp-lens--joint::before    { background: linear-gradient(90deg, #C6A86B, #F59E0B); }
.tp-lens--together::before { background: linear-gradient(90deg, #10B981, #3B82F6); }
.tp-lens__tag { font-size: 10px; letter-spacing: 0.28em; font-weight: 800; color: #78716C; margin-bottom: 10px; }
.tp-lens__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 24px; font-weight: 500; letter-spacing: -0.01em; color: #0F1E3A; margin: 0 0 8px; }
.tp-lens__body { font-size: 13.5px; line-height: 1.55; color: #57534E; margin: 0; }
.tp-usecases { padding: 40px 28px 72px; text-align: center; }
.tp-usecases__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; max-width: 900px; margin: 0 auto; }
@media (max-width: 900px) { .tp-usecases__grid { grid-template-columns: repeat(2, 1fr); } }
.tp-usecase { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-radius: 999px; background: #FBF7EE; border: 1px solid rgba(198,168,107,0.28); font-size: 13.5px; font-weight: 500; color: #0F1E3A; text-align: left; }
.tp-usecase__dot { width: 6px; height: 6px; border-radius: 999px; background: #C6A86B; box-shadow: 0 0 8px rgba(198,168,107,0.6); flex-shrink: 0; }
.tp-privacy { background: linear-gradient(180deg, #0F1E3A 0%, #0A1424 100%); color: #F1F5F9; padding: 72px 0; }
.tp-privacy__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(30px, 4.4vw, 46px); font-weight: 500; letter-spacing: -0.02em; line-height: 1.05; color: #F1F5F9; margin: 4px 0 36px; }
.tp-privacy__grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
@media (max-width: 720px) { .tp-privacy__grid { grid-template-columns: 1fr; } }
.tp-privacy__card { padding: 22px 24px; background: rgba(255,255,255,0.03); border: 1px solid rgba(201,169,110,0.18); border-radius: 18px; }
.tp-privacy__card-title { font-size: 13px; font-weight: 700; color: #E6D5A8; letter-spacing: 0.02em; margin-bottom: 8px; }
.tp-privacy__card-body { font-size: 14px; line-height: 1.6; color: rgba(241,245,249,0.78); margin: 0; }
.tp-cta { padding: 72px 28px; text-align: center; }
.tp-cta__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(30px, 4vw, 42px); font-weight: 500; letter-spacing: -0.02em; color: #0F1E3A; margin: 0 0 10px; }
.tp-cta__sub { font-size: 15.5px; line-height: 1.55; color: #57534E; max-width: 520px; margin: 0 auto 24px; }
.tp-cta__row { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; }
.tp-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 999px; font-size: 14px; font-weight: 600; letter-spacing: 0.01em; text-decoration: none; transition: transform .18s cubic-bezier(0.16,1,0.3,1), background-color .18s cubic-bezier(0.16,1,0.3,1); }
.tp-btn--gold { background: linear-gradient(180deg, #E6D5A8, #C6A86B); color: #0F1E3A; box-shadow: 0 12px 26px -8px rgba(198,168,107,0.55); }
.tp-btn--gold:hover { transform: translateY(-1px); }
.tp-btn--ghost { background: transparent; color: #0F1E3A; border: 1px solid #0F1E3A; }
.tp-btn--ghost:hover { background: #0F1E3A; color: #F6F3EC; }
.tp-cta__fine { font-size: 12px; color: #78716C; margin-top: 18px; }
@media (prefers-reduced-motion: reduce) { .tp-lens, .tp-btn { transition: none; } }
`;

/* ─────────── /together/couples ─────────── */
export function CouplesPage() {
    return (
        <MarketingPage
            slug="together-couples"
            title="VAULT Together · Couples"
            description="One financial life. Two individual people. Individual logins, shared household, personal privacy."
            canonical="/together/couples"
            hero={COUPLES_PAGE.hero}
            sub={COUPLES_PAGE.sub}
            statusKey={COUPLES_PAGE.statusKey}
            bullets={COUPLES_PAGE.bullets}
            tabs={COUPLES_PAGE.tabs}
            disclaimer="Granular privacy is a first-class feature: Private, Shared Summary, Shared Full and Selected Members — enforced server-side, on every read."
        />
    );
}

/* ─────────── /together/families ─────────── */
export function FamiliesPage() {
    return (
        <MarketingPage
            slug="together-families"
            title="VAULT Together · Families"
            description="Keep the family's financial world organised — documents, obligations, insurance, subscriptions and important dates."
            canonical="/together/families"
            hero={FAMILIES_PAGE.hero}
            sub={FAMILIES_PAGE.sub}
            statusKey={FAMILIES_PAGE.statusKey}
            bullets={FAMILIES_PAGE.bullets}
            disclaimer={FAMILIES_PAGE.disclaimer}
        />
    );
}

/* ─────────── /together/goals ─────────── */
export function TogetherGoalsPage() {
    return (
        <MarketingPage
            slug="together-goals"
            title="Shared Goals"
            description="Personal, couple and family goals — you set the target and the timeline, VAULT never recommends amounts."
            canonical="/together/goals"
            hero={GOALS_PAGE.hero}
            sub={GOALS_PAGE.sub}
            statusKey={GOALS_PAGE.statusKey}
            bullets={GOALS_PAGE.bullets}
            disclaimer={GOALS_PAGE.disclaimer}
        />
    );
}

/* ─────────── /together/privacy ─────────── */
export function TogetherPrivacyPage() {
    return (
        <MarketingPage
            slug="together-privacy"
            title="Household Privacy"
            description="Sharing should never mean giving up privacy. Private, Shared Summary, Shared Full or Selected Members."
            canonical="/together/privacy"
            hero={PRIVACY_PAGE.hero}
            sub={PRIVACY_PAGE.sub}
            statusKey={PRIVACY_PAGE.statusKey}
            bullets={PRIVACY_PAGE.bullets}
            tabs={PRIVACY_PAGE.tabs}
            disclaimer="Every visibility label is enforced server-side. Private records never appear in another member's household view — VAULT never relies on the browser to hide anything."
        />
    );
}

/* ─────────── /calendar (public) ─────────── */
export function CalendarPublicPage() {
    return (
        <MarketingPage
            slug="calendar-public"
            title="Financial Calendar"
            description="Your financial life has dates. VAULT remembers them."
            canonical="/calendar"
            hero={CALENDAR_PAGE.hero}
            sub={CALENDAR_PAGE.sub}
            statusKey={CALENDAR_PAGE.statusKey}
            bullets={CALENDAR_PAGE.bullets}
            primaryCta={{ label: CALENDAR_PAGE.cta, to: CALENDAR_PAGE.ctaTo, testid: "cta-calendar" }}
        />
    );
}

/* ─────────── /inbox (public) ─────────── */
export function InboxPublicPage() {
    return (
        <MarketingPage
            slug="inbox-public"
            title="VAULT Inbox"
            description="Drop it into VAULT. Statement, receipt, policy, invoice or warranty — we'll organise the rest."
            canonical="/inbox"
            hero={INBOX_PAGE.hero}
            sub={INBOX_PAGE.sub}
            statusKey={INBOX_PAGE.statusKey}
            bullets={INBOX_PAGE.bullets}
        />
    );
}

/* ─────────── /health (public) ─────────── */
export function HealthPublicPage() {
    return (
        <MarketingPage
            slug="health-public"
            title="VAULT Health"
            description="How organised is your VAULT? A transparent score of your records — never your wealth."
            canonical="/health"
            hero={HEALTH_PAGE.hero}
            sub={HEALTH_PAGE.sub}
            statusKey={HEALTH_PAGE.statusKey}
            bullets={HEALTH_PAGE.bullets}
            disclaimer={HEALTH_PAGE.disclaimer}
        />
    );
}

/* ─────────── /intelligence (public) ─────────── */
export function IntelligencePublicPage() {
    return (
        <MarketingPage
            slug="intelligence-public"
            title="Ask VAULT"
            description="Your VAULT already has the information. Just ask."
            canonical="/intelligence"
            hero={INTELLIGENCE_PAGE.hero}
            sub={INTELLIGENCE_PAGE.sub}
            statusKey={INTELLIGENCE_PAGE.statusKey}
            bullets={INTELLIGENCE_PAGE.bullets}
            disclaimer={INTELLIGENCE_PAGE.disclaimer}
        />
    );
}

/* ─────────── /about ─────────── */
export function AboutPage() {
    return (
        <MarketingPage
            slug="about"
            title="About"
            description="We're building a simpler way to organise financial life."
            canonical="/about"
            hero={ABOUT_PAGE.hero}
            sub={ABOUT_PAGE.sub}
            bullets={ABOUT_PAGE.bullets}
        />
    );
}

/* ─────────── /resources ─────────── */
export function ResourcesPage() {
    return (
        <MarketingPage
            slug="resources"
            title="Resources"
            description="Guides, updates and organisational how-tos from the VAULT team. No regulated advice."
            canonical="/resources"
            hero="Learn how to organise a modern financial life."
            sub="Practical guides and product notes from the VAULT team. VAULT never provides regulated financial, tax or legal advice."
        >
            <section className="max-w-4xl mx-auto px-6 pb-6" data-testid="resources-articles">
                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Latest guides</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    {Object.entries(ARTICLES).map(([slug, a]) => (
                        <Link key={slug} to={`/resources/${slug}`}
                              className="rounded-xl border border-stone-200 bg-white p-4 hover:border-stone-500 transition-colors"
                              data-testid={`resource-article-${slug}`}>
                            <div className="text-[10.5px] uppercase tracking-widest text-amber-800 font-semibold">{a.category}</div>
                            <div className="text-[14px] font-semibold text-stone-900 mt-1 leading-snug">{a.title}</div>
                            <div className="text-[12px] text-stone-500 mt-1.5 line-clamp-2">{a.summary}</div>
                            <div className="text-[11px] text-stone-400 mt-2">{a.readTime} read</div>
                        </Link>
                    ))}
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="resources-categories">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {RESOURCES_CATEGORIES.map(c => (
                        <div key={c} className="rounded-xl border border-stone-200 bg-white p-4">
                            <div className="text-[13px] font-semibold text-stone-900">{c}</div>
                            <div className="text-[11.5px] text-stone-500 mt-1">Coming soon — sign up to be notified when this category launches.</div>
                        </div>
                    ))}
                </div>
            </section>
        </MarketingPage>
    );
}

/* ─────────── /stories ─────────── */
export function StoriesPage() {
    return (
        <MarketingPage
            slug="stories"
            title="Customer Stories"
            description="Only verified testimonials from real VAULT customers."
            canonical="/stories"
            hero="Real people, keeping their financial life organised."
            sub="Every story here is a verified customer. VAULT never publishes fake or fabricated testimonials."
        >
            <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="stories-list">
                <div className="rounded-xl border border-stone-200 bg-white p-8 text-center">
                    <div className="text-[13px] text-stone-500">Customer stories are curated from the central testimonials registry.
                        See the homepage for the current featured testimonials, or <Link to="/help/share-experience" className="text-amber-800 font-semibold">share your own experience</Link>.
                    </div>
                </div>
            </section>
        </MarketingPage>
    );
}
